import { useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, X } from 'lucide-react';
import { useCreatePharmacySaleReturn } from '../../services/httpServices/queries';
import type {
  PharmacySale,
  PharmacySalePaymentMethod,
} from '../../services/httpServices/pharmacySaleService';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';

interface PharmacySaleReturnDialogProps {
  sale: PharmacySale;
  onClose: () => void;
}

const money = (value: number) => `৳${(Number(value) || 0).toLocaleString()}`;

const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const REFUND_METHODS: { value: PharmacySalePaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance', label: 'Insurance' },
];

/**
 * Return/refund dialog for a counter sale. Quantities are validated against the
 * un-returned balance per line; the refund defaults to the value of the returned
 * lines but can be lowered (e.g. exchanges) as long as it stays refundable.
 */
export function PharmacySaleReturnDialog({ sale, onClose }: PharmacySaleReturnDialogProps) {
  const createReturn = useCreatePharmacySaleReturn();

  const items = sale.items ?? [];

  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [refundAmount, setRefundAmount] = useState('');
  const [refundTouched, setRefundTouched] = useState(false);
  const [refundMethod, setRefundMethod] = useState<PharmacySalePaymentMethod>('cash');
  const [refundReference, setRefundReference] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const remainingFor = (item: (typeof items)[number]) =>
    round(Number(item.quantity) - Number(item.returnedQuantity ?? 0));

  const requestedLines = useMemo(
    () =>
      items
        .map((item) => ({
          item,
          quantity: round(Number(quantities[item.id] ?? 0)),
        }))
        .filter((line) => line.quantity > 0),
    [items, quantities],
  );

  const suggestedRefund = useMemo(
    () =>
      round(
        requestedLines.reduce((sum, line) => {
          const sold = round(Number(line.item.quantity));
          if (sold <= 0) return sum;
          return sum + (round(Number(line.item.totalPrice)) * line.quantity) / sold;
        }, 0),
      ),
    [requestedLines],
  );

  const stillRefundable = round(
    Math.max(0, Number(sale.paidAmount) - Number(sale.refundedAmount ?? 0)),
  );
  const maxRefund = round(Math.min(suggestedRefund, stillRefundable));

  useEffect(() => {
    if (!refundTouched) setRefundAmount(String(maxRefund));
  }, [maxRefund, refundTouched]);

  const parsedRefund = refundTouched ? Number(refundAmount || 0) : maxRefund;
  const refundInvalid = Number.isNaN(parsedRefund) || parsedRefund < 0 || parsedRefund > maxRefund;
  const hasSelection = requestedLines.length > 0;
  const canSubmit = hasSelection && !refundInvalid && !createReturn.isPending;

  const setQuantity = (id: string, value: string) =>
    setQuantities((prev) => ({ ...prev, [id]: value }));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await createReturn.mutateAsync({
        id: sale.id,
        data: {
          items: requestedLines.map((line) => ({
            saleItemId: line.item.id,
            quantity: line.quantity,
          })),
          refundAmount: parsedRefund,
          refundMethod,
          refundReference: refundReference.trim() || undefined,
          reason: reason.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      });
      onClose();
    } catch {
      // Error toast is shown by the global MutationCache; keep the dialog open.
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <RotateCcw size={18} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-text">Return / Refund</h2>
              <p className="text-xs text-text-muted font-mono">{sale.saleNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background/50 border-b border-border">
                  <th className="px-3 py-2 text-left text-xs text-text-muted uppercase">Item</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Sold</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Returnable</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase w-24">Return</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => {
                  const remaining = remainingFor(item);
                  return (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-text">{item.itemName}</td>
                      <td className="px-3 py-2 text-right text-text-muted">
                        {Number(item.quantity)}
                      </td>
                      <td className="px-3 py-2 text-right text-text-muted">
                        {remaining > 0 ? remaining : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          max={remaining}
                          step="any"
                          disabled={remaining <= 0}
                          value={quantities[item.id] ?? ''}
                          placeholder="0"
                          onChange={(e) => setQuantity(item.id, e.target.value)}
                          className="w-20 px-2 py-1 border border-border rounded-lg bg-background text-text text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-40"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-border bg-background/50 p-3 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Value of returned items</span>
              <span className="text-text font-medium">{money(suggestedRefund)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Refundable (paid)</span>
              <span className="text-text font-medium">{money(stillRefundable)}</span>
            </div>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Refund amount</label>
              <div className="flex items-center gap-2">
                <ValidatedNumberInput
                  max={maxRefund}
                  value={refundAmount}
                  onChange={(v) => {
                    setRefundTouched(true);
                    setRefundAmount(String(v));
                  }}
                  className={`flex-1 px-3 py-2 border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                    refundInvalid ? 'border-danger' : 'border-border'
                  }`}
                />
                {refundTouched && (
                  <button
                    type="button"
                    onClick={() => setRefundTouched(false)}
                    className="text-xs text-primary hover:underline whitespace-nowrap"
                  >
                    Use {money(maxRefund)}
                  </button>
                )}
              </div>
              {refundInvalid && (
                <p className="mt-1 text-xs text-danger">
                  Refund must be between 0 and {money(maxRefund)}.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Method</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value as PharmacySalePaymentMethod)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {REFUND_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Reference</label>
                <input
                  type="text"
                  value={refundReference}
                  onChange={(e) => setRefundReference(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Damaged packaging"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Note</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={createReturn.isPending}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {createReturn.isPending && <Loader2 size={16} className="animate-spin" />}
            Record Return
          </button>
        </div>
      </div>
    </div>
  );
}
